// src/pages/PageMenu/SettingsMenu.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  ButtonBase,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import SettingsIcon from '@mui/icons-material/Settings';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentsIcon from '@mui/icons-material/Payments';
import PrintIcon from '@mui/icons-material/Print';
import SecurityIcon from '@mui/icons-material/Security';

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function SettingsMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';

  const generalSettings = [
    {
      name: t('dashboard.generalSettings') || "إعدادات عامة",
      icon: <SettingsIcon />,
      color: "#4F46E5",
      bgColor: "rgba(79, 70, 229, 0.06)",
      link: "/settings"
    },
    {
      name: "إعدادات نقطة البيع",
      icon: <PointOfSaleIcon />,
      color: "#8B5CF6",
      bgColor: "rgba(139, 92, 246, 0.06)",
      link: "/pos-settings"
    },
    {
      name: "إعدادات الفاتورة الإلكترونية",
      icon: <SecurityIcon />,
      color: "#10B981",
      bgColor: "rgba(16, 185, 129, 0.06)",
      link: "/zatca-settings"
    },
    {
      name: "إعدادات الحسابات العامة",
      icon: <AccountBalanceIcon />,
      color: "#2563EB",
      bgColor: "rgba(37, 99, 235, 0.06)",
      link: "/gl-settings"
    },
  ].filter(Boolean);

  const advancedSettings = [
    {
      name: "أنواع الضرائب",
      icon: <PercentIcon />,
      color: "#F59E0B",
      bgColor: "rgba(245, 158, 11, 0.06)",
      link: "/vat-types"
    },
    {
      name: "طرق الدفع",
      icon: <PaymentsIcon />,
      color: "#EC4899",
      bgColor: "rgba(236, 72, 153, 0.06)",
      link: "/payment-methods"
    },
    {
      name: "أنواع الفواتير",
      icon: <ReceiptIcon />,
      color: "#3B82F6",
      bgColor: "rgba(59, 130, 246, 0.06)",
      link: "/invoice-types"
    },
    {
      name: "تصميم الفواتير",
      icon: <PrintIcon />,
      color: "#64748B",
      bgColor: "rgba(100, 116, 139, 0.06)",
      link: "/print-layout"
    },
  ].filter(Boolean);

  // دالة عرض الأيقونات المحدثة والمثبتة بحد أقصى 4 كروت في الصف الواحد لجميع الشاشات
  const renderGridItems = (items) => {
    return (
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',          // جوال: كرتين بالصف
            sm: 'repeat(3, 1fr)',          // تابلت: 3 كروت بالصف
            md: 'repeat(4, 1fr)',          // لابتوب: 4 كروت كحد أقصى
            lg: 'repeat(4, 1fr)',          // شاشات مكتبية عريضة: تثبيت 4 كروت بالصف
          },
          width: '100%'
        }}
      >
        {items.map((item, index) => (
          <ButtonBase
            key={index}
            onClick={() => navigate(item.link)}
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              p: { xs: 3, sm: 4 },
              borderRadius: '24px', // زوايا دائرية عصرية وناعمة متوافقة مع النظام
              backgroundColor: "#ffffff",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 20px -6px rgba(15, 23, 42, 0.04)',
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-5px)",
                boxShadow: `0 20px 25px -5px ${item.bgColor}, 0 10px 10px -5px rgba(0, 0, 0, 0.01)`,
                borderColor: item.color,
                "& .icon-container": {
                  transform: 'scale(1.08) rotate(3deg)',
                  backgroundColor: item.color,
                  color: '#ffffff'
                }
              },
              "&:active": { transform: "translateY(-1px)" }
            }}
          >
            {/* حاوية الأيقونة الدائرية التفاعلية */}
            <Box
              className="icon-container"
              sx={{
                width: { xs: 55, sm: 65 },
                height: { xs: 55, sm: 65 },
                borderRadius: '18px',
                backgroundColor: item.bgColor,
                color: item.color,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                transition: 'all 0.3s ease',
                '& svg': { fontSize: { xs: 26, sm: 30 } }
              }}
            >
              {item.icon}
            </Box>

            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                color: '#1E293B',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                letterSpacing: '-0.3px',
                lineHeight: 1.4,
                px: 0.5
              }}
            >
              {item.name}
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    );
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 5, 
        mb: 5, 
        px: { xs: 2, md: 4 },
      }} 
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* هيدر تدرج النصوص الفاخر الموحد لواجهات النظام */}
      <Box sx={{ mb: 6, textAlign: { xs: 'center', md: 'start' } }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          sx={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            letterSpacing: '-0.5px',
            mb: 1
          }}
        >
          {t('dashboard.generalSettings') || "الإعدادات"}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
         
        </Typography>
      </Box>

      {/* إعدادات النظام */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          ⚙️ إعدادات النظام
        </Typography>
        {renderGridItems(generalSettings)}
      </Box>
      
      {/* خيارات متقدمة */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h6" 
          sx={{
            fontWeight: 800, 
            mb: 3, 
            color: '#1E293B',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.2rem',
            '&::after': {
              content: '""',
              flexGrow: 1,
              height: '1px',
              backgroundColor: 'rgba(226, 232, 240, 0.8)',
              marginLeft: isRTL ? 0 : 2,
              marginRight: isRTL ? 2 : 0
            }
          }}
        >
          🛠️ خيارات متقدمة
        </Typography>
        {renderGridItems(advancedSettings)}
      </Box>
    </Container>
  );
}